import { createContext, memo, Suspense, useEffect, useReducer, useState } from 'react';

// third-party
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';


// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { AuthBaseGetApi, LoginApi, LogOutApi, SessionDeleteApi } from '../services/apiService';
import { AuthApi } from '../apis/authapi';
import { DecryptJweToken, UMEncrypt } from '../services/EncDecService';
import useGeoLocation from '../services/geoLocation';
import { showNotification } from '../services/notification';
import { sessionDataDec, sessionDataEnc } from '../services/sessionServiceEndDec';
import { safeLazy } from '../routes/safeLazy';
import { closeSnackbar } from 'notistack';

// import JWTContextRestPassword from './JWTContextRestPassword';
// const MemoizedJWTContextRestPassword = memo(JWTContextRestPassword);

const JWTContextRestPassword = safeLazy(() => import('./JWTContextRestPassword'));
const MemoizedJWTContextRestPassword = memo(JWTContextRestPassword);

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  userData: null
};


const setSession = (token) => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    sessionStorage.removeItem('token');
    sessionStorage.clear();
    closeSnackbar();
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [decodedData, setDecodedData] = useState(null);
  const { latLong, setLocation } = useGeoLocation();
  const [loginLoader, setLoginLoader] = useState(false);
  const [resetRequiredData, setResetRequiredData] = useState(null);

  useEffect(() => {
    setLocation();
  }, []);

  useEffect(() => {
    if (latLong) {
      console.log(latLong);
    }
  }, [latLong]);

  useEffect(() => {

    if (!decodedData) {

      const rawDashboardStr = sessionDataDec('dashboardData');

      if (rawDashboardStr && rawDashboardStr !== 'undefined' && rawDashboardStr !== 'null') {
        try {
          const dashboardData = JSON.parse(rawDashboardStr);
          setDecodedData(dashboardData);
        } catch (e) {
          setDecodedData(null);
        }
      }
    }

    if (decodedData) {
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          userData: decodedData
        }
      });
    }
    else {
      dispatch({
        type: LOGOUT,
        payload: {
          isLoggedIn: false,
          userData: null
        }
      });
    }

  }, [decodedData]);

  const login = async (username, password) => {
    setLoginLoader(true);
    setResetRequiredData(null)
    const encryptedUsername = UMEncrypt(username);
    const encryptedPassword = UMEncrypt(password);
    let params = new URLSearchParams();
    params.append('grant_type', 'password');
    params.append("username", encryptedUsername);
    params.append("password", encryptedPassword);

    try {
      const res = await LoginApi(AuthApi.url.login, params.toString());
      sessionDataEnc("token", res.access_token);
      sessionDataEnc('refresh_token', res.refresh_token);
      let jweToken = await DecryptJweToken(res.access_token)
      const tokenData = jwtDecode(jweToken);

      if (res.statusId === -1) {
        showNotification(res.statusDesc, 'error');
      } else {
        sessionDataEnc('isPasswordResetRequired', tokenData.isPasswordResetRequired);
        if (tokenData.isPasswordResetRequired) {
          // setLoginLoader(false);
          setResetRequiredData({
            username: username,
            mobileNumber: tokenData.mobileNumber
          });
          return;
        } else if (tokenData.authorities[0] == 'ROLE_SUPER_ADMIN') {
          dashboard();
        } else {
          fetchFeatureApi();
        }
      }
    } catch (err) {
      setLoginLoader(false);
      let errordata = err.response.data

      if (errordata.error_description == 'An active session already exists for the user on another device.' ||
        errordata.error_description == 'Maximum Device Reached. Please try again later.') {
        clearSession(username);
      }

      showNotification(errordata.error_description, "error");
    }

  }

  const clearSession = async (username) => {
    setLoginLoader(true);

    let body = {
      userName: username
    }

    let reqBody = JSON.stringify(body)

    try {
      const res = await SessionDeleteApi(AuthApi.url.clearSession, reqBody);
    } catch (err) {
    }
    finally {
      setLoginLoader(false);
    }
  };

  const fetchFeatureApi = () => {
    const catArr = [];
    catArr.push('GIFT');
    catArr.push('GPR');

    sessionDataEnc('cardCatArr', JSON.stringify(catArr));
    dashboard();
  };

  const dashboard = async () => {
    try {
      const res = await AuthBaseGetApi(AuthApi.url.dashboard);
      sessionDataEnc('dashboardData', JSON.stringify(res.userInfo));
      let dashboardData = res.userInfo
      setDecodedData(dashboardData);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          userData: dashboardData
        }
      });
      showNotification('Login Success', "success");

    } catch (err) {
      showNotification(err?.response?.data?.message, "error");
    }
    finally {
      setLoginLoader(false);
    }
  }

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.sessionDataDec('users') !== undefined && window.sessionDataDec('users') !== null) {
      const localUsers = window.sessionDataDec('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.sessionDataEnc('users', JSON.stringify(users));
  };

  const logout = async () => {
    const token = sessionDataDec('token'); // ← was 'CORE_SESSION', match your login

    if (!token) {
      cleanup();
      return;
    }

    const jweToken = await DecryptJweToken(token);
    const { exp } = jwtDecode(jweToken);

    const now = new Date();
    const expDate = new Date(exp * 1000);

    if (expDate <= now) {
      // Token expired — skip API call
      cleanup();
    } else {
      try {
        await LogOutApi(AuthApi.url.logout, {});
      } catch (err) {
      } finally {
        cleanup();
      }
    }
  };

  const cleanup = () => {
    setSession(null);
    dispatch({
      type: LOGOUT,
      payload: {
        isLoggedIn: false,
        userData: null
      }
    });
  };

  const resetPassword = async (email) => {
  };

  const updateProfile = () => { };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <>
      <JWTContext.Provider value={{ ...state, login, logout, register, resetPassword, updateProfile, loginLoader }}>
        {children}
        {resetRequiredData && (
          <Suspense fallback={null}>
            <MemoizedJWTContextRestPassword
              username={resetRequiredData.username}
              mobileNumber={resetRequiredData.mobileNumber}
              setLoginLoader={setLoginLoader}
            />
          </Suspense>
        )}
      </JWTContext.Provider>
    </>
  );
};

export default JWTContext;